import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { formatCurrency, getResponsiveThumbnailUrl, getStatusInfo } from '@/lib/utils';
import { Package, Gavel, Clock, Plus, TrendingUp, TrendingDown, Loader2, Trash2, Pencil, ArrowLeftRight, CheckCircle } from 'lucide-react';
import { listingsAPI, ordersAPI } from '@/lib/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import EditListingDialog from './EditListingDialog';

const DashboardMyListingsTab = ({ filterStatus = null }) => {
    const [listings, setListings] = useState([]);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState(filterStatus || 'all');
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    
    // Price reduction state
    const [reducePriceOpen, setReducePriceOpen] = useState(false);
    const [selectedListing, setSelectedListing] = useState(null);
    const [newPrice, setNewPrice] = useState('');
    const [reducing, setReducing] = useState(false);
    
    // Delete state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [listingToDelete, setListingToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    
    // Mark as sold state
    const [markingSold, setMarkingSold] = useState(null);
    const [markAsSoldDialogOpen, setMarkAsSoldDialogOpen] = useState(false);
    const [listingToMarkSold, setListingToMarkSold] = useState(null);
    
    // Edit state
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [listingToEdit, setListingToEdit] = useState(null);
    const [openedFromCard, setOpenedFromCard] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [listingsRes, ordersRes] = await Promise.all([
                    listingsAPI.getMy(),
                    ordersAPI.getAll('seller')  // Get orders where user is seller
                ]);
                setListings(listingsRes.data || []);
                setOrders(ordersRes.data || []);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Auto-open edit dialog when edit param is in URL
    useEffect(() => {
        const editId = searchParams.get('edit');
        if (editId && listings.length > 0 && !loading) {
            const listing = listings.find(l => l.listing_id === editId);
            if (listing) {
                setListingToEdit(listing);
                setEditDialogOpen(true);
                setOpenedFromCard(true);
                // Remove edit param from URL
                searchParams.delete('edit');
                setSearchParams(searchParams, { replace: true });
            }
        }
    }, [listings, loading, searchParams, setSearchParams]);
    
    // Handle edit dialog close - go back if opened from card
    const handleEditDialogClose = (open) => {
        setEditDialogOpen(open);
        if (!open && openedFromCard) {
            setOpenedFromCard(false);
            navigate(-1);
        }
    };

    const getTimeRemaining = (endDate) => {
        if (!endDate) return 'N/A';
        const end = new Date(endDate);
        const now = new Date();
        const diff = end - now;
        
        if (diff <= 0) return 'Încheiată';
        
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        
        if (days > 0) return `${days}z ${hours}h`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    // Filter listings based on filterStatus prop
    // "active" = listings still for sale (excluding auctions - they have their own tab)
    // "sold" = sold listings with completed orders
    const baseListings = filterStatus === 'active' 
        ? listings.filter(l => l.status === 'active' && l.listing_type !== 'auction')
        : filterStatus === 'sold'
            ? listings.filter(l => l.status === 'sold')
            : listings.filter(l => {
                if (l.status === 'active') return true;
                if (l.status === 'sold') {
                    const order = orders.find(o => o.listing_id === l.listing_id);
                    if (order) {
                        const finalStatuses = ['completed', 'cancelled'];
                        return !finalStatuses.includes(order.order_status);
                    }
                    return true;
                }
                return false;
            });

    const auctions = baseListings.filter(l => l.listing_type === 'auction');
    const sales = baseListings.filter(l => l.listing_type === 'sale' || l.listing_type === 'direct_sale');
    
    const filteredListings = filter === 'all' ? baseListings : 
                            filter === 'auctions' ? auctions : 
                            filter === 'active' ? listings.filter(l => l.status === 'active') :
                            filter === 'sold' ? listings.filter(l => l.status === 'sold') : sales;
    
    // Determine title based on filterStatus
    const tabTitle = filterStatus === 'active' ? 'De vânzare' : 
                     filterStatus === 'sold' ? 'Vândute' : 'Vânzări';
    
    // Get order status for a listing
    const getOrderStatus = (listingId) => {
        const order = orders.find(o => o.listing_id === listingId);
        return order ? order.order_status : null;
    };
    
    // Open price reduction modal
    const openReducePrice = (listing, e) => {
        e.stopPropagation();
        setSelectedListing(listing);
        const currentPrice = listing.listing_type === 'auction' 
            ? (listing.current_price || listing.starting_price || 0)
            : (listing.price || listing.current_price || listing.buy_now_price || 0);
        setNewPrice(Math.floor(currentPrice * 0.9).toString()); // Default 10% reduction
        setReducePriceOpen(true);
    };
    
    // Handle price reduction
    const handleReducePrice = async () => {
        if (!selectedListing || !newPrice) return;
        
        const priceNum = parseFloat(newPrice);
        const currentPrice = selectedListing.listing_type === 'auction'
            ? (selectedListing.current_price || selectedListing.starting_price || 0)
            : (selectedListing.price || selectedListing.current_price || selectedListing.buy_now_price || 0);
        
        if (priceNum >= currentPrice) {
            toast.error(`Noul preț trebuie să fie mai mic decât ${formatCurrency(currentPrice)}`);
            return;
        }
        
        if (priceNum <= 0) {
            toast.error('Prețul trebuie să fie mai mare decât 0');
            return;
        }
        
        setReducing(true);
        try {
            const response = await listingsAPI.reducePrice(selectedListing.listing_id, priceNum);
            toast.success(response.data.message || 'Prețul a fost redus cu succes');
            
            // Update local state
            setListings(prev => prev.map(l => {
                if (l.listing_id === selectedListing.listing_id) {
                    return {
                        ...l,
                        ...(l.listing_type === 'auction' 
                            ? { starting_price: priceNum, current_price: priceNum }
                            : { buy_now_price: priceNum, price: priceNum }
                        ),
                        previous_price: currentPrice,
                        discount_percent: response.data.discount_percent
                    };
                }
                return l;
            }));
            
            setReducePriceOpen(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la reducerea prețului');
        } finally {
            setReducing(false);
        }
    };
    
    // Open delete dialog
    const openDeleteDialog = (listing, e) => {
        e.stopPropagation();
        setListingToDelete(listing);
        setDeleteDialogOpen(true);
    };
    
    // Open edit dialog
    const openEditDialog = (listing, e) => {
        e.stopPropagation();
        setListingToEdit(listing);
        setEditDialogOpen(true);
    };
    
    // Handle edit success - refresh listings
    const handleEditSuccess = async () => {
        try {
            const listingsRes = await listingsAPI.getMy();
            setListings(listingsRes.data || []);
        } catch (error) {
            console.error('Error refreshing listings:', error);
        }
        // Navigate back if opened from card
        if (openedFromCard) {
            setOpenedFromCard(false);
            navigate(-1);
        }
    };
    
    // Handle listing deletion
    const handleDeleteListing = async () => {
        if (!listingToDelete) return;
        
        setDeleting(true);
        try {
            await listingsAPI.delete(listingToDelete.listing_id);
            toast.success('Anunțul a fost șters cu succes');
            
            // Remove from local state
            setListings(prev => prev.filter(l => l.listing_id !== listingToDelete.listing_id));
            setDeleteDialogOpen(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la ștergerea anunțului');
        } finally {
            setDeleting(false);
        }
    };
    
    // Handle mark as sold - show confirmation dialog first
    const openMarkAsSoldDialog = (listing, e) => {
        e.stopPropagation();
        setListingToMarkSold(listing);
        setMarkAsSoldDialogOpen(true);
    };
    
    // Confirm mark as sold
    const handleMarkAsSold = async () => {
        if (!listingToMarkSold) return;
        
        setMarkingSold(listingToMarkSold.listing_id);
        try {
            await listingsAPI.update(listingToMarkSold.listing_id, { status: 'sold' });
            toast.success('Piesa a fost marcată ca vândută');
            
            // Update local state
            setListings(prev => prev.map(l => 
                l.listing_id === listingToMarkSold.listing_id ? { ...l, status: 'sold' } : l
            ));
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la marcarea ca vândută');
        } finally {
            setMarkingSold(null);
            setMarkAsSoldDialogOpen(false);
            setListingToMarkSold(null);
        }
    };

    if (loading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-10 w-full" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                        <Skeleton key={i} className="h-48" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <Card className="rounded-2xl">
            <CardHeader className="pb-4">
                <div>
                    <CardTitle className="flex items-center gap-2">
                        <Package className={`w-5 h-5 ${filterStatus === 'active' ? 'text-green-500' : filterStatus === 'sold' ? 'text-blue-500' : 'text-primary'}`} />
                        {tabTitle}
                    </CardTitle>
                    <CardDescription>
                        {auctions.length} licitații • {sales.length} vânzări directe
                    </CardDescription>
                </div>

                {/* Filter tabs - hidden on mobile when filterStatus is set */}
                {!filterStatus && (
                    <div className="hidden sm:flex gap-2 mt-4">
                        <Button 
                            variant={filter === 'all' ? 'default' : 'outline-solid'} 
                            size="sm" 
                            onClick={() => setFilter('all')}
                            className="rounded-full"
                        >
                            Toate ({baseListings.length})
                        </Button>
                        <Button 
                            variant={filter === 'auctions' ? 'default' : 'outline-solid'} 
                            size="sm" 
                            onClick={() => setFilter('auctions')}
                            className="rounded-full"
                        >
                            <Gavel className="w-3 h-3 mr-1" />
                            Licitații ({auctions.length})
                        </Button>
                        <Button 
                            variant={filter === 'sales' ? 'default' : 'outline-solid'} 
                            size="sm" 
                            onClick={() => setFilter('sales')}
                            className="rounded-full"
                        >
                            <Package className="w-3 h-3 mr-1" />
                            Vânzări ({sales.length})
                        </Button>
                    </div>
                )}
            </CardHeader>
            <CardContent>
                {filteredListings.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                        <Package className="w-12 h-12 mx-auto mb-4 opacity-30" />
                        <p>Nu ai nicio piesă listată</p>
                        <p className="text-sm mt-2">Creează prima ta listare pentru a vinde piese de colecție</p>
                        <div className="flex gap-2 justify-center mt-4">
                            <Button 
                                onClick={() => navigate('/create-listing?type=auction')} 
                                className="bg-primary hover:bg-primary/90 text-white"
                            >
                                <Gavel className="w-4 h-4 mr-2" />
                                Licitație nouă
                            </Button>
                            <Button 
                                onClick={() => navigate('/create-listing?type=sale')}
                                className="bg-primary hover:bg-primary/90 text-white"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Vânzare nouă
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {filteredListings.map((listing) => {
                            const isAuction = listing.listing_type === 'auction';
                            // Find order for this listing (if sold)
                            const order = orders.find(o => o.listing_id === listing.listing_id);
                            // Link to order if sold, otherwise to listing
                            const linkTo = listing.status === 'sold' && order 
                                ? `/order/${order.order_id}`
                                : `/listing/${listing.slug || listing.listing_id}`;
                            const thumbnailUrl = listing.images?.[0] ? getResponsiveThumbnailUrl(listing.images[0], 200, 150, 75) : null;
                            return (
                                <Card 
                                    key={listing.listing_id} 
                                    className="rounded-2xl cursor-pointer hover:shadow-md transition-shadow"
                                    onClick={() => navigate(linkTo)}
                                >
                                    <CardContent className="p-3 sm:p-4">
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                            <div className="flex items-center gap-3 sm:gap-4">
                                                {thumbnailUrl ? (
                                                    <img
                                                        src={thumbnailUrl}
                                                        alt={listing.title}
                                                        className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl object-cover shrink-0"
                                                        loading="lazy"
                                                    />
                                                ) : (
                                                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-gray-100 flex items-center justify-center shrink-0">
                                                        {isAuction ? (
                                                            <Gavel className="w-6 h-6 sm:w-8 sm:h-8 text-muted-foreground" />
                                                        ) : (
                                                            <Package className="w-6 h-6 sm:w-8 sm:h-8 text-muted-foreground" />
                                                        )}
                                                    </div>
                                                )}
                                                <div className="min-w-0 flex-1">
                                                    <p className="font-semibold text-sm sm:text-base truncate">{listing.title}</p>
                                                    <p className="text-xs sm:text-sm text-muted-foreground">
                                                        {isAuction ? 'Licitație' : 'Vânzare directă'}
                                                    </p>
                                                    <div className="flex flex-wrap items-center gap-1 mt-1">
                                                        {(() => {
                                                            // For sold listings, show the actual order status
                                                            if (listing.status === 'sold' && order) {
                                                                const statusInfo = getStatusInfo(order.order_status);
                                                                return (
                                                                    <Badge className={`text-xs ${
                                                                        statusInfo.color === 'success' ? 'bg-green-50 text-green-700' :
                                                                        statusInfo.color === 'primary' ? 'bg-blue-50 text-blue-700' :
                                                                        statusInfo.color === 'warning' ? 'bg-orange-50 text-orange-700' :
                                                                        statusInfo.color === 'error' ? 'bg-red-50 text-red-700' :
                                                                        'bg-gray-50 text-gray-700'
                                                                    }`}>
                                                                        {statusInfo.label}
                                                                    </Badge>
                                                                );
                                                            }
                                                            // For active listings
                                                            if (listing.status === 'active') {
                                                                return (
                                                                    <Badge className="text-xs bg-green-50 text-green-700">
                                                                        {isAuction ? 'Activă' : 'Disponibil'}
                                                                    </Badge>
                                                                );
                                                            }
                                                            // For ended or other statuses
                                                            return (
                                                                <Badge className="text-xs bg-gray-50 text-gray-700">
                                                                    {listing.status === 'ended' ? 'Încheiată' : listing.status}
                                                                </Badge>
                                                            );
                                                        })()}
                                                        {listing.accepts_trades && (
                                                            <Badge className="text-xs bg-orange-50 text-orange-600">
                                                                <ArrowLeftRight className="w-3 h-3 mr-1" />
                                                                Schimb
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-left sm:text-right ml-17 sm:ml-0">
                                                <p className="font-semibold text-primary">
                                                    {listing.status === 'sold' && order 
                                                        ? formatCurrency(order.amount || order.product_amount)
                                                        : formatCurrency(isAuction 
                                                            ? (listing.final_price || listing.current_price || listing.starting_price || 0) 
                                                            : (listing.price || listing.current_price || 0))
                                                    }
                                                </p>
                                                {isAuction && listing.status === 'active' && (
                                                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 justify-end">
                                                        <Clock className="w-3 h-3" />
                                                        {getTimeRemaining(listing.end_date)}
                                                    </div>
                                                )}
                                                {isAuction && (
                                                    <p className="text-xs text-muted-foreground">
                                                        {listing.bid_count || 0} oferte
                                                    </p>
                                                )}
                                                {/* Action buttons - only for active listings */}
                                                {listing.status === 'active' && (
                                                    <div className="flex flex-col sm:flex-row gap-1 sm:gap-2 mt-2">
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="text-blue-600 border-blue-200 hover:bg-blue-50 text-xs px-2"
                                                            onClick={(e) => openEditDialog(listing, e)}
                                                            data-testid="edit-listing-btn"
                                                        >
                                                            <Pencil className="w-3 h-3 mr-1" />
                                                            Editează
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="text-orange-600 border-orange-200 hover:bg-orange-50 text-xs px-2"
                                                            onClick={(e) => openReducePrice(listing, e)}
                                                            data-testid="reduce-price-btn"
                                                        >
                                                            <TrendingDown className="w-3 h-3 mr-1" />
                                                            Reducere
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="text-green-600 border-green-200 hover:bg-green-50 text-xs px-2"
                                                            onClick={(e) => openMarkAsSoldDialog(listing, e)}
                                                            disabled={markingSold === listing.listing_id}
                                                            data-testid="mark-sold-btn"
                                                        >
                                                            {markingSold === listing.listing_id ? (
                                                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                            ) : (
                                                                <CheckCircle className="w-3 h-3 mr-1" />
                                                            )}
                                                            Vândută
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="text-red-600 border-red-200 hover:bg-red-50 text-xs px-2"
                                                            onClick={(e) => openDeleteDialog(listing, e)}
                                                            data-testid="delete-listing-btn"
                                                        >
                                                            <Trash2 className="w-3 h-3 mr-1" />
                                                            Șterge
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}
            </CardContent>
            
            {/* Price Reduction Modal */}
            <Dialog open={reducePriceOpen} onOpenChange={setReducePriceOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <TrendingDown className="w-5 h-5 text-orange-500" />
                            Reducere preț
                        </DialogTitle>
                        <DialogDescription>
                            Reduce prețul pentru &quot;{selectedListing?.title}&quot;. Utilizatorii care au acest produs în favorite vor fi notificați.
                        </DialogDescription>
                    </DialogHeader>
                    
                    {selectedListing && (
                        <div className="space-y-4 py-4">
                            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                                <span className="text-sm text-muted-foreground">Preț actual:</span>
                                <span className="font-semibold">
                                    {formatCurrency(
                                        selectedListing.listing_type === 'auction'
                                            ? (selectedListing.current_price || selectedListing.starting_price || 0)
                                            : (selectedListing.price || selectedListing.current_price || selectedListing.buy_now_price || 0)
                                    )}
                                </span>
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="newPrice">Noul preț (RON)</Label>
                                <Input
                                    id="newPrice"
                                    type="number"
                                    value={newPrice}
                                    onChange={(e) => setNewPrice(e.target.value)}
                                    placeholder="Introdu noul preț"
                                    min="1"
                                />
                            </div>
                            
                            {newPrice && parseFloat(newPrice) > 0 && (
                                <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-orange-700">Reducere:</span>
                                        <span className="font-semibold text-orange-700">
                                            -{Math.round(
                                                ((
                                                    (selectedListing.listing_type === 'auction'
                                                        ? (selectedListing.current_price || selectedListing.starting_price || 0)
                                                        : (selectedListing.price || selectedListing.current_price || selectedListing.buy_now_price || 0)
                                                    ) - parseFloat(newPrice)
                                                ) / (
                                                    selectedListing.listing_type === 'auction'
                                                        ? (selectedListing.current_price || selectedListing.starting_price || 1)
                                                        : (selectedListing.price || selectedListing.current_price || selectedListing.buy_now_price || 1)
                                                )) * 100
                                            )}%
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setReducePriceOpen(false)}>
                            Anulează
                        </Button>
                        <Button 
                            onClick={handleReducePrice}
                            disabled={reducing || !newPrice}
                            className="bg-orange-600 hover:bg-orange-700"
                        >
                            {reducing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Aplică reducerea
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Delete Confirmation Modal */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <Trash2 className="w-5 h-5" />
                            Șterge anunțul
                        </DialogTitle>
                        <DialogDescription>
                            Ești sigur că vrei să ștergi anunțul &quot;<strong>{listingToDelete?.title}</strong>&quot;? 
                            Această acțiune nu poate fi anulată.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                            Anulează
                        </Button>
                        <Button 
                            variant="destructive"
                            onClick={handleDeleteListing}
                            disabled={deleting}
                        >
                            {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Șterge definitiv
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Mark as Sold Confirmation Modal */}
            <Dialog open={markAsSoldDialogOpen} onOpenChange={setMarkAsSoldDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-green-600">
                            <CheckCircle className="w-5 h-5" />
                            Confirmă vânzarea
                        </DialogTitle>
                        <DialogDescription className="space-y-3 pt-2">
                            <p>
                                Ești sigur că piesa &quot;<strong>{listingToMarkSold?.title}</strong>&quot; a fost vândută?
                            </p>
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                                <p className="font-medium mb-1">💡 Recomandare</p>
                                <p>
                                    Pentru a avea piesa în evidența vânzărilor și a beneficia de sistemul de rating, 
                                    îți recomandăm să finalizezi vânzarea prin circuitul platformei (cumpărătorul să plaseze comanda prin site).
                                </p>
                            </div>
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setMarkAsSoldDialogOpen(false)}>
                            Anulează
                        </Button>
                        <Button 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={handleMarkAsSold}
                            disabled={markingSold}
                        >
                            {markingSold && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Marchează ca vândută
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Edit Listing Dialog */}
            <EditListingDialog
                listing={listingToEdit}
                open={editDialogOpen}
                onOpenChange={handleEditDialogClose}
                onSuccess={handleEditSuccess}
            />
        </Card>
    );
};

export default DashboardMyListingsTab;
